/**
 * Financial reconciliation (§11).
 *
 * The statement this screen exists to prove:
 *
 *     opening liability + credits − debits = closing liability
 *
 * It is shown as an actual worked calculation rather than five stat cards,
 * because the point is not the numbers - it is that they add up. An operator
 * should be able to read down the column and check the arithmetic themselves.
 *
 * Two independent checks sit beneath it:
 *   GATEWAY VARIANCE - money Razorpay took for top-ups vs the cash we credited.
 *   LEDGER INTEGRITY - every cached account balance vs the sum of its ledger.
 *
 * `balanced` is the single boolean an operator acts on. When it is false,
 * something is genuinely unexplained and the offending accounts are listed.
 */
import { useCallback, useEffect, useState } from 'react'
import { AlertTriangle, CheckCircle2, RefreshCw } from 'lucide-react'

import LoadErrorState from '../../components/common/LoadErrorState'
import { Card, CardTitle, PillButton, StatCard, StatRow, TableHead, Td } from '../../features/b2b/components/primitives'
import { useColors } from '../../hooks/useColors'
import { formatINR, getReconciliation } from '../../services/walletApi'
import type { DateRangeFilter, ReconciliationReport } from '../../services/walletApi'

export default function ReconciliationTab({ filter }: { filter: DateRangeFilter }) {
  const colors = useColors()
  const [report, setReport] = useState<ReconciliationReport | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      setReport(await getReconciliation(filter))
    } catch {
      setError('Could not run the reconciliation.')
    } finally {
      setLoading(false)
    }
  }, [filter])

  useEffect(() => {
    void load()
  }, [load])

  if (error) return <LoadErrorState message={error} onRetry={load} />
  if (!report) return <Card><span style={{ color: colors.textMuted }}>Running reconciliation…</span></Card>

  const statementRows: Array<{ label: string; value: number; sign?: '+' | '−'; rule?: boolean; hint?: string }> = [
    { label: 'Opening wallet liability', value: report.opening_liability, hint: 'Everything owed before this period began' },
    { label: 'Wallet credits', value: report.credits, sign: '+' },
    { label: 'Wallet debits', value: report.debits, sign: '−' },
    { label: 'Closing wallet liability', value: report.closing_liability, rule: true },
  ]

  return (
    <>
      {/* The verdict, before the detail. */}
      <Card
        style={{
          marginBottom: '18px',
          borderLeft: `4px solid ${report.balanced ? '#15803D' : '#991B1B'}`,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {report.balanced
              ? <CheckCircle2 size={22} color="#15803D" />
              : <AlertTriangle size={22} color="#991B1B" />}
            <div>
              <div style={{ fontWeight: 700, fontSize: '16px', color: report.balanced ? '#15803D' : '#991B1B' }}>
                {report.balanced ? 'Every rupee is accounted for' : 'Unexplained difference found'}
              </div>
              <div style={{ fontSize: '13px', color: colors.textMuted }}>
                {report.integrity.accounts_checked} wallet{report.integrity.accounts_checked === 1 ? '' : 's'} checked
                against the ledger · gateway variance {formatINR(report.gateway_reconciliation.variance)}
              </div>
            </div>
          </div>
          <PillButton variant="secondary" onClick={load} disabled={loading}>
            <RefreshCw size={14} /> Re-run
          </PillButton>
        </div>
      </Card>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: '16px' }}>
        <Card>
          <CardTitle>Wallet liability statement</CardTitle>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              {statementRows.map((row) => (
                <tr
                  key={row.label}
                  style={row.rule ? { borderTop: `2px solid ${colors.textPrimary}` } : undefined}
                >
                  <Td strong={row.rule}>
                    <div>{row.label}</div>
                    {row.hint ? (
                      <div style={{ fontSize: '12px', color: colors.textMuted }}>{row.hint}</div>
                    ) : null}
                  </Td>
                  <Td align="right" strong={row.rule}>
                    {row.sign ? `${row.sign} ` : ''}{formatINR(row.value)}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>

          <div style={{ fontSize: '12px', color: colors.textMuted, marginTop: '12px' }}>
            Live balance right now: <strong>{formatINR(report.live_liability)}</strong>. This matches the
            closing figure only when the selected period runs up to today.
          </div>
        </Card>

        <Card>
          <CardTitle>What the liability is made of</CardTitle>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              <tr>
                <Td>
                  <div>Customer-funded credits</div>
                  <div style={{ fontSize: '12px', color: colors.textMuted }}>Money customers paid. Refundable</div>
                </Td>
                <Td align="right" strong>{formatINR(report.breakdown.customer_funded_credits)}</Td>
              </tr>
              <tr>
                <Td>
                  <div>Promotional credits</div>
                  <div style={{ fontSize: '12px', color: colors.textMuted }}>Top-up bonuses and giveaways. Our cost</div>
                </Td>
                <Td align="right" strong>{formatINR(report.breakdown.promotional_credits)}</Td>
              </tr>
              <tr>
                <Td>
                  <div>Referral rewards</div>
                  <div style={{ fontSize: '12px', color: colors.textMuted }}>Paid to referrers and their friends</div>
                </Td>
                <Td align="right" strong>{formatINR(report.breakdown.referral_rewards)}</Td>
              </tr>
              <tr>
                <Td>
                  <div>Refund credits</div>
                  <div style={{ fontSize: '12px', color: colors.textMuted }}>Returned from cancelled orders</div>
                </Td>
                <Td align="right" strong>{formatINR(report.breakdown.refund_credits)}</Td>
              </tr>
              <tr>
                <Td>
                  <div>Manual credits</div>
                  <div style={{ fontSize: '12px', color: colors.textMuted }}>Adjustments posted by an admin</div>
                </Td>
                <Td align="right" strong>{formatINR(report.breakdown.admin_credits)}</Td>
              </tr>
              <tr style={{ borderTop: `1px solid ${colors.border}` }}>
                <Td>
                  <div>Spent on orders</div>
                  <div style={{ fontSize: '12px', color: colors.textMuted }}>Liability discharged as laundry</div>
                </Td>
                <Td align="right" strong>− {formatINR(report.breakdown.order_spend)}</Td>
              </tr>
            </tbody>
          </table>
        </Card>
      </div>

      <div style={{ marginTop: '18px' }}>
        <StatRow>
          <StatCard
            label="Gateway collected (top-ups)"
            value={formatINR(report.gateway_reconciliation.gateway_collected)}
            hint={`${report.gateway_reconciliation.topup_count} paid top-ups`}
          />
          <StatCard
            label="Credited as customer money"
            value={formatINR(report.gateway_reconciliation.gateway_collected - report.gateway_reconciliation.variance)}
            hint="Should equal what the gateway collected"
          />
          <StatCard
            label="Gateway variance"
            value={formatINR(report.gateway_reconciliation.variance)}
            color={report.gateway_reconciliation.variance === 0 ? '#15803D' : '#991B1B'}
            hint={report.gateway_reconciliation.variance === 0
              ? 'Every payment produced a matching credit'
              : 'A payment was captured without a credit, or the reverse'}
          />
        </StatRow>
      </div>

      {/* Only rendered when there is something wrong. A permanently-empty
          "discrepancies" table trains people to ignore it. */}
      {report.integrity.mismatches.length > 0 ? (
        <Card>
          <CardTitle>Accounts whose cached balance disagrees with their ledger</CardTitle>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <TableHead
              columns={[
                { key: 'user', label: 'Customer' },
                { key: 'cached', label: 'Cached balance', align: 'right' },
                { key: 'ledger', label: 'Ledger balance', align: 'right' },
                { key: 'diff', label: 'Difference', align: 'right' },
              ]}
            />
            <tbody>
              {report.integrity.mismatches.map((mismatch) => (
                <tr key={mismatch.user_id}>
                  <Td>
                    <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>{mismatch.user_id}</span>
                  </Td>
                  <Td align="right">{mismatch.cached_balance === null ? '-' : formatINR(mismatch.cached_balance)}</Td>
                  <Td align="right">{formatINR(mismatch.ledger_balance)}</Td>
                  <Td align="right" strong style={{ color: '#991B1B' }}>
                    {formatINR((mismatch.cached_balance ?? 0) - mismatch.ledger_balance)}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      ) : null}
    </>
  )
}
